import { useEffect, useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import useSWR from 'swr'

import { CaseStudyFeedDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { isRichText, serializeCacheKey } from '@/lib/contentful/utils'

import { isDevelopment } from '@/utils/isDevelopment'
import getLocalizedPathFromEnglish from '@/utils/locales/localized-routes/getLocalizedPathFromEnglish'

import { Button } from '@/components/Button'
import { Image } from '@/components/Image'
import { Warning } from '@/components/Warning'

import { useCaseStudyFilters } from '../CaseStudyFilters/useCaseStudyFilters'

export const DEFAULT_LIMIT = 12

type Props = {
	className?: string
	cardCtaText?: string
	loadMoreCtaText?: string
}

export function CaseStudyFeed({ className, cardCtaText, loadMoreCtaText }: Props) {
	const { locale, isPreview } = useRouter()

	const { appliedIndustry, appliedTopics, appliedCompetitors } = useCaseStudyFilters()

	const [skip, setSkip] = useState(0)

	const { data, error } = useSWR(
		serializeCacheKey('caseStudyFeed', {
			limit: DEFAULT_LIMIT,
			locale,
			skip,
			industry: appliedIndustry?.title,
			topics: appliedTopics.map((activeTopic) => activeTopic.title),
			competitors: appliedCompetitors.map((activeCompetitor) => activeCompetitor.title)
		}),
		() =>
			client({ preview: isPreview }).request(CaseStudyFeedDocument, {
				where: {
					...(appliedIndustry?.title ? { industriesList: { name: appliedIndustry.title } } : {}),
					...(appliedTopics.length > 0
						? { topicsList: { name_in: appliedTopics.map((activeTopic) => activeTopic.title) } }
						: {}),
					...(appliedCompetitors.length > 0
						? {
								competitorsList: {
									name_in: appliedCompetitors.map((activeCompetitor) => activeCompetitor.title)
								}
						  }
						: {}),
					status: true
				},
				limit: DEFAULT_LIMIT,
				skip,
				locale,
				preview: isPreview
			})
	)

	const [total, setTotal] = useState(data?.caseStudyCollection?.total ?? 0)
	const [items, setItems] = useState(data?.caseStudyCollection?.items ?? [])

	useEffect(() => {
		if (!data?.caseStudyCollection) return

		setItems((prev) => [...prev?.slice(0, skip), ...(data?.caseStudyCollection?.items ?? [])])

		setTotal(data.caseStudyCollection.total)
	}, [data, skip])

	useEffect(() => {
		setSkip(0)
	}, [appliedIndustry, appliedTopics, appliedCompetitors])

	if (error) {
		if (isDevelopment()) {
			return <Warning>{error?.message || 'An error occurred'}</Warning>
		}
		if (!items || items.length === 0) {
			if (isDevelopment()) {
				return <Warning>No case studies found</Warning>
			}
			return <p className="text-gray-600 text-xl text-center">No results found</p>
		}
	}

	return (
		<div className={className}>
			<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-7.5 gap-y-10 sm:gap-y-15 lg:gap-y-20">
				{items.map((caseStudy) => {
					if (!caseStudy) return null

					return (
						<div
							key={caseStudy.sys.id}
							data-testid="case-study-feed-item"
							className="relative group"
						>
							<Link
								href={`${getLocalizedPathFromEnglish(locale, '/case-study/')}${caseStudy.slug}/`}
								className="absolute inset-0 z-10 peer"
							>
								<span className="sr-only">Read Case Study</span>
							</Link>

							{caseStudy.image?.url && (
								<div className="relative aspect-video">
									<Image
										src={caseStudy.image.url}
										alt={caseStudy.image.title || ''}
										fill
										sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
										className="object-cover"
									/>
								</div>
							)}

							{caseStudy.title && <h3 className="text-xl font-medium mt-6">{caseStudy.title}</h3>}

							{isRichText(caseStudy.summary) && (
								<p className="mt-2.5 text-sm text-gray-500">
									{documentToPlainTextString(caseStudy.summary.json)}
								</p>
							)}

							<Button
								variant="subtle"
								color="secondary"
								showIcon
								fakeButton
								className="mt-6 group-hover:text-bc-blue"
							>
								{cardCtaText ?? 'Read story'}
							</Button>
						</div>
					)
				})}
			</div>

			{items?.length < total && (
				<div className="mt-25 flex justify-center">
					<Button
						onClick={() => {
							if (items?.length < total) {
								setSkip((prev) => prev + DEFAULT_LIMIT)
							}
						}}
					>
						{loadMoreCtaText ?? 'See more'}
					</Button>
				</div>
			)}
		</div>
	)
}
